'use strict';

/**
 * Two-step withdrawal form (EU right of withdrawal, art. 11a CRD).
 *
 * Step 1 collects the details, step 2 shows them back for review and only the
 * explicit confirmation button submits. Submitting POSTs urlencoded data to the
 * app proxy at /apps/herroeping. Any failure falls back to an e-mail address so
 * a withdrawal can never hit a dead end.
 */
(function () {
  var TIME_ZONE = 'Europe/Amsterdam';
  var LOCALE_TAGS = { nl: 'nl-NL', de: 'de-DE', en: 'en-GB' };

  function localeTag(root) {
    var iso = (root.getAttribute('data-locale') || 'en').toLowerCase().split('-')[0];
    return LOCALE_TAGS[iso] || 'en-GB';
  }

  function formatDate(date, tag) {
    try {
      return new Intl.DateTimeFormat(tag, {
        timeZone: TIME_ZONE,
        day: 'numeric',
        month: 'long',
        year: 'numeric'
      }).format(date);
    } catch (e) {
      return date.toLocaleDateString();
    }
  }

  function formatTime(date, tag) {
    try {
      return new Intl.DateTimeFormat(tag, {
        timeZone: TIME_ZONE,
        hour: '2-digit',
        minute: '2-digit'
      }).format(date);
    } catch (e) {
      return date.toLocaleTimeString();
    }
  }

  function init(root) {
    if (root.dataset.herroepingReady === 'true') return;
    root.dataset.herroepingReady = 'true';

    var endpoint = root.getAttribute('data-endpoint') || '/apps/herroeping';
    var tag = localeTag(root);

    var form = root.querySelector('[data-herroeping-form]');
    var steps = {
      form: root.querySelector('[data-herroeping-step="form"]'),
      review: root.querySelector('[data-herroeping-step="review"]'),
      success: root.querySelector('[data-herroeping-step="success"]')
    };
    var errorBox = root.querySelector('[data-herroeping-error]');
    var errorText = root.querySelector('[data-herroeping-error-text]');
    var confirmButton = root.querySelector('[data-herroeping-confirm]');
    var editButton = root.querySelector('[data-herroeping-edit]');

    if (!form || !steps.form || !steps.review || !steps.success || !confirmButton) return;

    var reviewFields = {};
    var nodes = root.querySelectorAll('[data-herroeping-review]');
    for (var i = 0; i < nodes.length; i++) {
      reviewFields[nodes[i].getAttribute('data-herroeping-review')] = nodes[i];
    }

    var emptyLabel = steps.review.getAttribute('data-empty-label') || '';
    var payload = null;

    function showStep(name) {
      Object.keys(steps).forEach(function (key) {
        steps[key].hidden = key !== name;
      });
      if (steps[name]) steps[name].focus();
    }

    function hideError() {
      if (errorBox) errorBox.hidden = true;
    }

    function showError(kind) {
      if (!errorBox) return;
      var attr = kind === 'validation' ? 'data-text-validation' : 'data-text-generic';
      if (errorText) {
        errorText.textContent = errorBox.getAttribute(attr) || errorBox.getAttribute('data-text-generic') || '';
      }
      errorBox.hidden = false;
      errorBox.focus();
    }

    function readForm() {
      var data = new FormData(form);
      return {
        name: (data.get('name') || '').toString().trim(),
        order_number: (data.get('order_number') || '').toString().trim(),
        email: (data.get('email') || '').toString().trim(),
        note: (data.get('note') || '').toString().trim(),
        locale: (data.get('locale') || '').toString(),
        website: (data.get('website') || '').toString()
      };
    }

    function fillReview(values) {
      Object.keys(reviewFields).forEach(function (key) {
        var value = values[key];
        reviewFields[key].textContent = value ? value : emptyLabel;
      });
    }

    function setSending(sending) {
      confirmButton.disabled = sending;
      var label = sending
        ? confirmButton.getAttribute('data-label-sending')
        : confirmButton.getAttribute('data-label-idle');
      if (label) confirmButton.textContent = label;
    }

    function showSuccess(submittedAt) {
      var date = submittedAt ? new Date(submittedAt) : new Date();
      if (isNaN(date.getTime())) date = new Date();

      var dateNode = root.querySelector('[data-herroeping-date]');
      var timeNode = root.querySelector('[data-herroeping-time]');
      if (dateNode) dateNode.textContent = formatDate(date, tag);
      if (timeNode) timeNode.textContent = formatTime(date, tag);

      showStep('success');
    }

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      if (typeof form.reportValidity === 'function' && !form.reportValidity()) return;

      hideError();
      payload = readForm();
      fillReview(payload);
      showStep('review');
    });

    if (editButton) {
      editButton.addEventListener('click', function () {
        hideError();
        showStep('form');
      });
    }

    confirmButton.addEventListener('click', function () {
      if (!payload) payload = readForm();

      hideError();
      setSending(true);

      var body = new URLSearchParams();
      ['name', 'order_number', 'email', 'note', 'locale', 'website'].forEach(function (key) {
        body.append(key, payload[key] || '');
      });

      fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8',
          Accept: 'application/json'
        },
        body: body.toString()
      })
        .then(function (response) {
          return response
            .json()
            .catch(function () {
              return {};
            })
            .then(function (data) {
              return { status: response.status, ok: response.ok, data: data };
            });
        })
        .then(function (result) {
          if (result.ok && result.data && result.data.ok === true) {
            showSuccess(result.data.submitted_at);
            return;
          }
          setSending(false);
          showError(result.status === 400 ? 'validation' : 'generic');
        })
        .catch(function () {
          setSending(false);
          showError('generic');
        });
    });
  }

  function initAll() {
    var roots = document.querySelectorAll('[data-herroeping]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  // Re-init when the section is re-rendered in the theme editor.
  document.addEventListener('shopify:section:load', initAll);
})();
